<template>
  <transition
    v-if="!noContent"
    :name="transitionName"
  >
    <div
      v-show="isActive"
      v-cloak
      ref="overlay"
      class="overlay critical-hide"
      :class="[
        $blockClass(blockClass),
        type,
        `${type}--${direction}`,
      ]"
      :style="{
        backgroundImage: `url(${backgroundImage})`,
      }"
    >
      <div
        v-if="template"
        ref="scrollElement"
        class="overlay__container"
        :class="[
          $blockClass(blockClass, 'container'),
          $blockClass(type, 'container'),
        ]"
      >
        <div
          class="overlay__header"
          :class="[
            $blockClass(blockClass, 'header'),
            $blockClass(type, 'header'),
          ]"
        >
          <p
            v-if="$slots.title"
            class="overlay__title text-heading-l"
            :class="[
              $blockClass(blockClass, 'title'),
              $blockClass(type, 'title'),
            ]"
          >
            <slot name="title" />
          </p>

          <button
            v-if="close"
            class="overlay__close icon-button"
            :class="[
              $blockClass(blockClass, 'close'),
              $blockClass(type, 'close'),
            ]"
            type="button"
            :aria-label="$string('accessibility.close')"
            @click.prevent="closeOverlay(namespace)"
          >
            <slot name="close">
              <icon-close />
            </slot>
          </button>
        </div>

        <div
          class="overlay__body"
          :class="[
            $blockClass(blockClass, 'body'),
            $blockClass(type, 'body'),
            { 'overlay__body--no-margin': !$slots.title },
          ]"
        >
          <slot
            :is-active="isActive"
            name="body"
          />
        </div>
      </div>

      <!-- If template prop is false then just render body slot -->
      <div
        v-else
        ref="scrollElement"
        class="overlay__container"
        :class="[
          $blockClass(blockClass, 'container'),
          $blockClass(type, 'container'),
        ]"
      >
        <slot
          :is-active="isActive"
          name="body"
        />
      </div>
    </div>
  </transition>

  <teleport
    :disabled="!teleport"
    to="body"
  >
    <transition name="fade">
      <button
        v-if="isActive && showOverlay"
        class="window-overlay"
        :class="namespace"
        tabindex="-1"
        type="button"
        aria-hidden="true"
        @click.prevent="closeOverlay(namespace)"
      />
    </transition>
  </teleport>
</template>

<script>

/**
 * Vue: Overlay
 * -----------------------------------------------------------------------------
 * Base template for overlays (drawers and modals).
 * - Add `js-overlay="scrollElement"` to element in <slot> to pass that as the
 *   element to disableBodyScroll instead of `.overlay__container`.
 *
 * @param {Boolean} [autoQueue] - Automatically opened overlays are queued.
 * @param {Number} [autoShow] - Time in ms to wait before auto-showing overlay.
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {Boolean} [close] Display close button.
 * @param {String} direction - Open animation direction set using transition
 * styles, `up` or `down` for `modal` and `left` or `right` for `drawer`.
 * @param {Boolean} [disableScroll] - Trigger `disableBodyScroll` on open.
 * @param {String} namespace - Unique namespace for overlay, reference when
 * opening/closing the overlay.
 * @param {Boolean} [noContent] - Displays no content, only window overlay, you
 * must manually disable scroll and trap focus in the component which triggers
 * this overlay.
 * @param {Boolean} [showOverlay] - Show the window overlay?
 * @param {Boolean} [teleport] - Teleport window overlay to end of <body>.
 * @param {Boolean} [template] - Render with default templating.
 * @param {String} type - Type of overlay, `modal`, `drawer`, or `shelf`.
 *
 */
import { clearAllBodyScrollLocks, disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import { nextTick } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'

import IconClose from '~icons/misc/close.svg'

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Overlay',

  components: {
    IconClose,
  },

  props: {
    autoQueue: {
      type: Boolean,
      default: true,
    },
    autoShow: {
      type: Number,
      default: 0,
    },
    blockClass: {
      type: String,
      default: '',
    },
    close: {
      type: Boolean,
      default: true,
    },
    direction: {
      type: String,
      default: 'up',
      required: true,
      validator: (value) => {
        return ['up', 'down', 'left', 'right'].includes(value)
      },
    },
    disableScroll: {
      type: Boolean,
      default: true,
    },
    namespace: {
      type: String,
      default: '',
      required: true,
    },
    noContent: {
      type: Boolean,
      default: false,
    },
    showOverlay: {
      type: Boolean,
      default: true,
    },
    teleport: {
      type: Boolean,
      default: true,
    },
    template: {
      type: Boolean,
      default: true,
    },
    type: {
      type: String,
      default: 'modal',
      required: true,
      validator: (value) => {
        return ['drawer', 'modal', 'shelf', 'fade'].includes(value)
      },
    },
    backgroundImage: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      cnvsGlobal: true,
      scrollElement: false,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
      getFirstElement: 'accessibility/getFirstElement',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      hydrated: (state) => state.index.hydrated,
      previousElement: (state) => state.accessibility.previousElement,
    }),

    /**
     * Compute overlay active state.
     * @returns {Boolean}
     */
    isActive() {
      return this.getActiveOverlay === this.namespace
    },

    /**
     * Compute transition name based on type.
     * @returns {String}
     */
    transitionName() {
      if (this.type === 'fade') {
        return 'fade'
      }

      const type = this.type === 'modal' ? 'fade' : 'slide'
      return `${type}-${this.direction}`
    },
  },

  watch: {

    /**
     * Watch hydrated state to start auto-show countdown.
     */
    hydrated() {
      this.setAutoShow()
    },

    /**
     * Watch active state.
     */
    isActive() {
      if (!this.disableScroll || this.noContent) {
        return
      }

      /**
       * If active then:
       * - Set previous element (to focus on close).
       * - Disable body scroll.
       * - Reset scroll element scroll position.
       * - Trap focus inside overlay element.
       * - Focus first trap focus element.
       */
      if (this.isActive) {
        this.getScrollElement()
        this.setPreviousElement(document.activeElement)
        disableBodyScroll(this.scrollElement)

        nextTick().then(() => {
          if (this.scrollElement) {
            this.scrollElement.scrollTo(0, 0)
          }

          this.updateFocusTrap()
        })

        return
      }

      if (this.scrollElement) {
        enableBodyScroll(this.scrollElement)
      } else {
        clearAllBodyScrollLocks()
      }

      /**
       * If no other active overlay when closing then clear focus trap.
       */
      if (!this.getActiveOverlay) {
        this.clearFocusTrap()
      }

      /**
       * Set focus back to previous element if set.
       */
      if (!this.previousElement || typeof this.previousElement !== 'object') {
        return
      }

      this.previousElement.focus()
      this.setPreviousElement()
    },
  },

  mounted() {
    this.setEventBusListeners()
    this.setAutoShow()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      clearFocusTrap: 'accessibility/clearFocusTrap',
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
      queueOverlay: 'overlays/queue',
      setFocusTrap: 'accessibility/setFocusTrap',
      setPreviousElement: 'accessibility/setPreviousElement',
    }),

    /**
     * Get scroll element.
     */
    getScrollElement() {
      if (!this.$refs.scrollElement) {
        this.scrollElement = false
        return
      }

      this.scrollElement =
        this.$refs.scrollElement.querySelector('[js-overlay="scrollElement"]') ||
        this.$refs.scrollElement
    },

    /**
     * Set modal to autoshow.
     */
    setAutoShow() {
      if (!this.autoShow || !this.hydrated) {
        return
      }

      setTimeout(() => {
        if (this.autoQueue) {
          this.queueOverlay({ namespace: this.namespace })
          return
        }

        this.openOverlay({ namespace: this.namespace })
      }, this.autoShow)
    },

    /**
     * Set EventBus listeners.
     */
    setEventBusListeners() {
      cnvs.EventBus.listen('overlay:update', (namespace) => {
        if (!this.isActive || namespace !== this.namespace) {
          return
        }

        this.updateFocusTrap()
        this.updateBodyScroll()
      })
    },

    /**
     * Update focus trap using Vuex.
     */
    updateFocusTrap() {
      this.setFocusTrap({ container: this.$refs.overlay })

      /**
       * Have to use timeout to wait for animation to complete.
       * - Safari doesn't support `preventScroll`.
       */
      setTimeout(() => {
        if (!this.getFirstElement) {
          return
        }

        this.getFirstElement.focus({ preventScroll: true })
      }, this.$timing('normal'))
    },

    /**
     * Update body scroll element.
     */
    updateBodyScroll() {
      if (this.scrollElement) {
        enableBodyScroll(this.scrollElement)
      } else {
        clearAllBodyScrollLocks()
      }

      this.getScrollElement()

      if (cnvs.storybook && !this.scrollElement) {
        return
      }

      disableBodyScroll(this.scrollElement)
    },
  },
}
</script>
